@switch (status()) {
  @case ('loading') {
    <div class="spinner"></div>
  }
  @case ('invalidLink') {
    <div class="title" i18n>This link does not work</div>
    <div class="message" i18n>Go back to your assistant and ask it to connect to Sytex again.</div>
  }
  @case ('loadFailed') {
    <div class="title" i18n>Authorize your assistant</div>
    <div class="error" i18n>Could not load organization list</div>
    <div class="buttons">
      <ng-container *ngTemplateOutlet="cancelButton"></ng-container>
      <span class="spacer"></span>
      <button class="rounded-button accent" type="button" (click)="retry()">
        <fa-icon [icon]="['far', 'redo']"></fa-icon>
        <span i18n>Try again</span>
      </button>
    </div>
    <div class="secondary-actions">
      <ng-container *ngTemplateOutlet="useAnotherAccountButton"></ng-container>
    </div>
  }
  @case ('noOrganizations') {
    <div class="title" i18n>Authorize your assistant</div>
    <div class="message" i18n>
      It seems that your user has been disabled and there are no active organizations associated with your
      account.
    </div>
    <div class="buttons">
      <ng-container *ngTemplateOutlet="cancelButton"></ng-container>
    </div>
    <div class="secondary-actions">
      <ng-container *ngTemplateOutlet="useAnotherAccountButton"></ng-container>
    </div>
  }
  @case ('noInstanceOrganizations') {
    <div class="title" i18n>Authorize your assistant</div>
    <div class="message" i18n>
      You have no organization on {{ mcpClient()?.instanceHost }}. Go back to your assistant, connect it again
      and choose the Sytex where you work.
    </div>
    <div class="buttons">
      <ng-container *ngTemplateOutlet="cancelButton"></ng-container>
    </div>
    <div class="secondary-actions">
      <ng-container *ngTemplateOutlet="useAnotherAccountButton"></ng-container>
    </div>
  }
  @case ('showingCode') {
    <div class="title" i18n>Your assistant is almost ready</div>
    <div class="message" i18n>Paste this code only into your assistant. Sytex will never ask you for it.</div>
    <div class="code-box">
      <span class="code sentry-mask">{{ authorization()?.pasteCode }}</span>
      <button class="rounded-button accent" type="button" (click)="copyCode()">
        <fa-icon [icon]="['far', isCopied() ? 'check' : 'copy']"></fa-icon>
        @if (isCopied()) {
          <span i18n="the code was copied|">Copied</span>
        } @else {
          <span i18n="action|">Copy</span>
        }
      </button>
    </div>
    <div class="hint" i18n>
      The code works once and expires in
      {expiresInMinutes(), plural, =1 {1 minute} other {{{ expiresInMinutes() }} minutes}}.
    </div>
  }
  @case ('returning') {
    <div class="title" i18n>Done!</div>
    <div class="message" i18n>You can go back to your assistant.</div>
    <div class="hint" i18n>
      If your browser shows an error page instead, go back to your assistant and ask it to connect to Sytex
      again.
    </div>
  }
  @case ('cancelled') {
    <div class="title" i18n>Nothing was authorized</div>
    <div class="message" i18n>You can close this tab.</div>
  }
  @default {
    @if (isMcp()) {
      <div class="title" i18n>An app is asking for access to your Sytex account</div>
    } @else {
      <div class="title" i18n>Authorize your assistant</div>
    }
    <div class="subtitle" i18n>It will be able to work in Sytex with your account.</div>

    <!-- In MCP mode who asks and where the access goes come first: the user
         must read them before the account they are handing over. -->
    <ng-template #account>
      <div class="detail">
        <div class="avatar"><fa-icon [icon]="['fas', 'envelope']"></fa-icon></div>
        <div class="info">
          <div class="label" i18n>Account</div>
          <div class="value">{{ email() }}</div>
        </div>
      </div>
    </ng-template>
    <div class="details">
      @if (mcpClient(); as client) {
        <div class="detail">
          <div class="avatar"><fa-icon [icon]="['fas', 'robot']"></fa-icon></div>
          <div class="info">
            <div class="label" i18n>Assistant</div>
            <!-- An unverified client's name is whatever it chose to call itself:
                 the host it comes from is who it is. -->
            <div class="value">
              {{ deviceName() }}
              @if (!client.isClientVerified && client.clientHost) {
                <ng-container i18n>· from {{ client.clientHost }}</ng-container>
              }
            </div>
            @if (!client.isClientVerified) {
              <div class="unverified">
                <fa-icon [icon]="['fal', 'info-circle']"></fa-icon>
                <span i18n>Sytex did not verify this app</span>
              </div>
            }
          </div>
        </div>
        <div class="detail">
          <div class="avatar"><fa-icon [icon]="['fas', 'map-marker-alt']"></fa-icon></div>
          <div class="info">
            <div class="label" i18n>Access goes to</div>
            <div class="value">
              @switch (client.destinationKind) {
                @case ('web') {
                  {{ client.destinationHost }}
                }
                @case ('app') {
                  <ng-container i18n>the {{ destinationAppName() }} app</ng-container>
                }
                @default {
                  <ng-container i18n>an app on this computer</ng-container>
                }
              }
            </div>
          </div>
        </div>
        <ng-container *ngTemplateOutlet="account"></ng-container>
      } @else {
        <ng-container *ngTemplateOutlet="account"></ng-container>
        <div class="detail">
          <div class="avatar"><fa-icon [icon]="['fas', 'laptop']"></fa-icon></div>
          <div class="info">
            <div class="label" i18n>Name the assistant gave itself</div>
            <div class="value">{{ deviceName() }}</div>
          </div>
        </div>
      }
      @if (!isPickingOrganization()) {
        <div class="detail">
          <div class="avatar"><fa-icon [icon]="['fas', 'building']"></fa-icon></div>
          <div class="info">
            <div class="label" i18n>Organization</div>
            <div class="value">{{ selectedOrganization()?.name }}</div>
            @if (otherOrganizationCount() > 0) {
              <!-- prettier-ignore -->
              <div class="hint" i18n>
                It will also be able to work in
                {otherOrganizationCount(), plural, =1 {1 other organization of yours} other {{{ otherOrganizationCount() }} other organizations of yours}}.
              </div>
            }
          </div>
          @if (organizations().length > 1) {
            <button class="link-button" type="button" (click)="changeOrganization()">
              <span i18n="action|">Change</span>
            </button>
          }
        </div>
      }
    </div>

    @if (isMcp() && !isPickingOrganization()) {
      <div class="organization-hint" i18n>
        {{ deviceName() }} will only work in <strong>{{ selectedOrganization()?.name }}</strong
        >. To use it in another organization, connect it again.
      </div>
    }

    @if (isPickingOrganization()) {
      @if (isMcp()) {
        <div class="organization-prompt" i18n>
          Choose the organization where <strong>{{ deviceName() }}</strong> will work. It will only work
          there.
        </div>
      } @else {
        <div class="label" i18n>Choose the organization to continue.</div>
      }
      <div class="organization-list">
        @for (
          organization of organizations();
          track organization.id + '|' + organization.identityProviderName
        ) {
          <a
            class="organization"
            [class.selected]="isSelected(organization)"
            (click)="selectOrganization(organization)">
            @if (organization.avatar) {
              <div class="avatar" [style.background-image]="'url(' + organization.avatar + ')'"></div>
            } @else {
              <div class="avatar without-image">
                <fa-icon [icon]="['fas', 'building']"></fa-icon>
              </div>
            }
            <div class="info">
              <div class="name">{{ organization.name }}</div>
              @if (showIdentityProvider(organization)) {
                <div class="identity-provider">{{ organization.identityProviderName }}</div>
              }
            </div>
            <fa-icon [icon]="['far', 'angle-right']"></fa-icon>
          </a>
        }
      </div>
    }

    <div class="hint caution">
      <span i18n>Only continue if you just asked your own assistant to connect to Sytex.</span>
    </div>

    <div class="buttons">
      <ng-container *ngTemplateOutlet="cancelButton"></ng-container>
      <span class="spacer"></span>
      @if (status() === 'authorizing') {
        <div class="spinner"></div>
      } @else {
        <button
          class="rounded-button accent"
          type="button"
          [disabled]="!canAuthorize()"
          (click)="authorize()">
          <fa-icon [icon]="['fas', 'arrow-right']"></fa-icon>
          <span i18n>Authorize</span>
        </button>
      }
    </div>
    @if (errorMessage()) {
      <div class="error">{{ errorMessage() }}</div>
    }
    <div class="secondary-actions">
      @if (canSignInWithOrganization()) {
        <button class="rounded-button accent" type="button" (click)="signInWithOrganization()">
          <fa-icon [icon]="['fas', 'building']"></fa-icon>
          <span i18n>Sign in with your organization</span>
        </button>
      }
      <ng-container *ngTemplateOutlet="useAnotherAccountButton"></ng-container>
    </div>
    @if (europeUrl) {
      <a class="region-link" [href]="europeUrl" i18n>My account is in Europe</a>
    }
  }
}

<ng-template #useAnotherAccountButton>
  <button
    class="secondary-button"
    type="button"
    (click)="useAnotherAccount()"
    [disabled]="status() === 'authorizing' || isSigningOut()">
    <fa-icon [icon]="['fas', 'user-lock']"></fa-icon>
    <span i18n>Use another account</span>
  </button>
</ng-template>

<ng-template #cancelButton>
  <button
    class="secondary-button"
    type="button"
    (click)="cancel()"
    [disabled]="status() === 'authorizing' || isSigningOut()">
    <fa-icon [icon]="['far', 'times']"></fa-icon>
    <span i18n>Cancel</span>
  </button>
</ng-template>
